import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../api/client";
import { formatDateTime } from "../i18n";
import { Card, Badge, EmptyState } from "../components/shared";
import {
  Building2, Search, Users, Activity, Calendar, Power, PowerOff,
  ShieldCheck, X, ShieldAlert, BarChart3, Check, Plus, KeyRound,
  RefreshCw, Copy, CreditCard, CalendarPlus, Gift, Pencil, Globe,
} from "lucide-react";
import { toast } from "sonner";

const INPUT = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-11 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";
const BTN_PRIMARY = "h-11 px-4 rounded-xl bg-brand text-white font-semibold text-sm hover:bg-brand/90 disabled:opacity-50 flex items-center gap-2";
const BTN_SECONDARY = "h-11 px-4 rounded-xl border border-[var(--border-default)] bg-white text-ink font-semibold text-sm hover:bg-subtle disabled:opacity-50 flex items-center gap-2";

export default function PlatformTenants() {
  const [tenants, setTenants] = useState([]);
  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, o, p] = await Promise.all([
        api.get("/platform/tenants"),
        api.get("/platform/overview"),
        api.get("/platform/plans"),
      ]);
      setTenants(t.data);
      setOverview(o.data);
      setPlans(p.data);
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = tenants;
    if (statusFilter !== "all") {
      list = list.filter((t) => (statusFilter === "active") === !!t.active);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((t) =>
        (t.name || "").toLowerCase().includes(s) ||
        (t.slug || "").toLowerCase().includes(s) ||
        (t.country || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [tenants, search, statusFilter]);

  const toggleActive = async (t) => {
    const action = t.active ? "suspend" : "activate";
    if (t.active && !window.confirm(`Suspendre la clinique "${t.name}" ?\n\nLes utilisateurs ne pourront plus se connecter.`)) return;
    try {
      await api.post(`/platform/tenants/${t.id}/${action}`, action === "suspend" ? { reason: "Admin action" } : {});
      toast.success(action === "suspend" ? "Clinique suspendue" : "Clinique activée");
      load();
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    }
  };

  const openDetail = async (t) => {
    try {
      const { data } = await api.get(`/platform/tenants/${t.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    const { data } = await api.get(`/platform/tenants/${detail.tenant.id}`);
    setDetail(data);
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-ink flex items-center gap-2">
            <Building2 className="w-7 h-7 text-brand" /> Cliniques partenaires
          </h1>
          <p className="text-ink-muted text-sm mt-1">Gestion multi-tenant · Activation · Abonnements · Agrégats uniquement</p>
        </div>
        <button data-testid="sa-create-tenant-btn" onClick={() => setShowCreate(true)} className={BTN_PRIMARY}>
          <Plus className="w-4 h-4" /> Créer une clinique
        </button>
      </div>

      {/* Overview */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="platform-overview">
          <OverviewCard icon={Building2} tone="brand" label="Total cliniques" value={overview.total_tenants} />
          <OverviewCard icon={Check} tone="success" label="Actives" value={overview.active_tenants} />
          <OverviewCard icon={ShieldAlert} tone={overview.suspended_tenants > 0 ? "warning" : "neutral"} label="Suspendues" value={overview.suspended_tenants} />
          <OverviewCard icon={Users} tone="neutral" label="Utilisateurs totaux" value={overview.total_users} />
        </div>
      )}

      {/* Filters */}
      <Card className="p-3 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input data-testid="sa-search" className={`${INPUT} pl-10`} placeholder="Rechercher par nom, slug, pays…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1 p-1 rounded-xl bg-subtle">
          {[
            { k: "all", label: `Toutes (${tenants.length})` },
            { k: "active", label: `Actives (${tenants.filter((t) => t.active).length})` },
            { k: "inactive", label: `Suspendues (${tenants.filter((t) => !t.active).length})` },
          ].map((f) => (
            <button key={f.k} onClick={() => setStatusFilter(f.k)} data-testid={`filter-${f.k}`}
              className={`h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${statusFilter === f.k ? "bg-white shadow-sm text-brand" : "text-ink-muted"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="tenants-table">
        {loading ? (
          <div className="py-16 text-center text-ink-muted">Chargement…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Building2} title="Aucune clinique" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-subtle text-xs uppercase text-ink-muted tracking-wider">
                  <th className="text-left py-3 px-5 font-semibold">Clinique</th>
                  <th className="text-left py-3 px-5 font-semibold">Plan · Statut</th>
                  <th className="text-right py-3 px-5 font-semibold">Utilisateurs</th>
                  <th className="text-right py-3 px-5 font-semibold">Patients</th>
                  <th className="text-right py-3 px-5 font-semibold">Consultations</th>
                  <th className="text-right py-3 px-5 font-semibold">Factures</th>
                  <th className="text-left py-3 px-5 font-semibold">Dernière activité</th>
                  <th className="text-right py-3 px-5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className={`border-t border-[var(--border-default)] cursor-pointer hover:bg-subtle/50 ${!t.active ? "bg-[#d34044]/5" : ""}`}
                    onClick={() => openDetail(t)} data-testid={`tenant-${t.id}`}>
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-heading font-bold ${t.active ? "bg-brand/10 text-brand" : "bg-[#d34044]/10 text-[#b12e31]"}`}>
                          {t.name?.[0] || "?"}
                        </div>
                        <div>
                          <div className="font-semibold text-ink">{t.name}</div>
                          <div className="text-xs text-ink-muted">{t.slug} · {t.country} · {t.currency}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-5">
                      {t.subscription ? (
                        <div className="flex flex-col gap-1">
                          <Badge tone="brand">{t.subscription.plan || "—"}</Badge>
                          <Badge tone={statusTone(t.subscription.status)}>{t.subscription.status}</Badge>
                        </div>
                      ) : <span className="text-ink-muted text-xs">—</span>}
                    </td>
                    <td className="py-3 px-5 text-right stat-number">{t.users_count}</td>
                    <td className="py-3 px-5 text-right stat-number">{t.patients_count}</td>
                    <td className="py-3 px-5 text-right stat-number">{t.consultations_count}</td>
                    <td className="py-3 px-5 text-right stat-number">{t.invoices_count}</td>
                    <td className="py-3 px-5 text-sm text-ink-muted">{t.last_activity ? formatDateTime(t.last_activity) : "—"}</td>
                    <td className="py-3 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-2">
                        <Badge tone={t.active ? "success" : "danger"}>{t.active ? "Active" : "Suspendue"}</Badge>
                        <button onClick={() => toggleActive(t)} data-testid={`tenant-toggle-${t.id}`} title={t.active ? "Suspendre" : "Activer"}
                          className={`p-2 rounded-lg ${t.active ? "hover:bg-terracotta/10 text-ink hover:text-terracotta" : "hover:bg-[#2d8a56]/10 text-ink hover:text-[#2d8a56]"}`}>
                          {t.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {detail && (
        <DetailModal
          data={detail}
          plans={plans}
          onClose={() => setDetail(null)}
          onRefresh={() => { refreshDetail(); load(); }}
        />
      )}
      {showCreate && (
        <CreateTenantModal
          plans={plans}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function statusTone(s) {
  if (s === "active") return "success";
  if (s === "trial") return "info";
  if (s === "read_only" || s === "past_due") return "warning";
  if (s === "cancelled") return "danger";
  return "neutral";
}

function OverviewCard({ icon: Icon, label, value, tone }) {
  const tones = {
    brand: "bg-brand/10 text-brand",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    neutral: "bg-subtle text-ink",
  };
  return (
    <Card className="p-4 flex items-start gap-3">
      <div className={`p-2.5 rounded-xl ${tones[tone] || tones.neutral}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-ink-muted uppercase tracking-wider">{label}</div>
        <div className="stat-number text-2xl font-heading font-extrabold text-ink">{value}</div>
      </div>
    </Card>
  );
}

/* ----------- Create Tenant Modal ----------- */
function CreateTenantModal({ plans, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", slug: "", country: "SN", currency: "XOF", timezone: "Africa/Dakar",
    owner_first_name: "", owner_last_name: "", owner_email: "", owner_phone: "",
    plan_id: plans.find((p) => p.popular)?.id || plans[0]?.id || "pro",
    temp_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      if (!payload.temp_password) delete payload.temp_password;
      const { data } = await api.post("/platform/tenants", payload);
      setResult(data);
      toast.success("Clinique créée");
    } catch (err) {
      toast.error(apiError(err, "Erreur lors de la création"));
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié"); } catch {}
  };

  if (result) {
    return (
      <Modal onClose={() => { onCreated(); }} title="Clinique créée">
        <div className="space-y-4" data-testid="sa-tenant-created">
          <div className="p-4 rounded-xl bg-[#2d8a56]/10 text-[#2d8a56] text-sm flex items-center gap-2">
            <Check className="w-4 h-4" /> La clinique et le compte propriétaire ont été créés.
          </div>
          <Field label="Clinique" v={<>{result.tenant.name} <span className="text-ink-muted">({result.tenant.slug})</span></>} />
          <Field label="Owner email" v={<div className="flex items-center gap-2"><span className="font-mono">{result.owner.email}</span>
            <button onClick={() => copy(result.owner.email)} className="p-1 hover:bg-subtle rounded"><Copy className="w-3 h-3" /></button></div>} />
          <div className="p-4 rounded-xl bg-[#e3a03b]/10 border border-[#e3a03b]/30">
            <div className="text-xs text-[#8a5a10] uppercase tracking-wider mb-1">Mot de passe temporaire (à transmettre)</div>
            <div className="flex items-center justify-between gap-2">
              <code className="font-mono text-lg text-ink" data-testid="sa-created-temp-pwd">{result.owner.temp_password}</code>
              <button onClick={() => copy(result.owner.temp_password)} className={BTN_SECONDARY}>
                <Copy className="w-4 h-4" /> Copier
              </button>
            </div>
            <div className="text-xs text-[#8a5a10] mt-2">Changement forcé à la première connexion.</div>
          </div>
          <div className="flex justify-end">
            <button onClick={onCreated} className={BTN_PRIMARY}>Fermer</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title="Créer une clinique">
      <form onSubmit={submit} className="space-y-4" data-testid="sa-create-tenant-form">
        <Section title="Clinique">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Nom *" value={form.name} onChange={set("name")} required testid="sa-ct-name" />
            <Input label="Slug (optionnel)" value={form.slug} onChange={set("slug")} placeholder="auto" testid="sa-ct-slug" />
            <Input label="Pays" value={form.country} onChange={set("country")} testid="sa-ct-country" />
            <Input label="Devise" value={form.currency} onChange={set("currency")} testid="sa-ct-currency" />
            <Input label="Fuseau" value={form.timezone} onChange={set("timezone")} testid="sa-ct-tz" />
            <div>
              <label className="text-xs text-ink-muted uppercase tracking-wider">Plan</label>
              <select className={INPUT} value={form.plan_id} onChange={set("plan_id")} data-testid="sa-ct-plan">
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — {p.price_xof} XOF/mois</option>
                ))}
              </select>
            </div>
          </div>
        </Section>
        <Section title="Propriétaire (owner admin)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Prénom *" value={form.owner_first_name} onChange={set("owner_first_name")} required testid="sa-ct-fn" />
            <Input label="Nom *" value={form.owner_last_name} onChange={set("owner_last_name")} required testid="sa-ct-ln" />
            <Input label="Email *" type="email" value={form.owner_email} onChange={set("owner_email")} required testid="sa-ct-email" />
            <Input label="Téléphone" value={form.owner_phone} onChange={set("owner_phone")} testid="sa-ct-phone" />
            <div className="sm:col-span-2">
              <Input label="Mot de passe temporaire (optionnel — généré sinon)" value={form.temp_password}
                onChange={set("temp_password")} placeholder="Laisser vide pour générer" testid="sa-ct-pwd" />
            </div>
          </div>
        </Section>
        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border-default)]">
          <button type="button" onClick={onClose} className={BTN_SECONDARY}>Annuler</button>
          <button type="submit" disabled={saving} className={BTN_PRIMARY} data-testid="sa-ct-submit">
            {saving ? "Création…" : "Créer la clinique"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------- Detail Modal with extended management ----------- */
function DetailModal({ data, plans, onClose, onRefresh }) {
  const { tenant: t, counts, last_activity, subscription } = data;
  const [resetResult, setResetResult] = useState(null);
  const [domainCheck, setDomainCheck] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);

  // Auto-fetch domain check once when the modal opens
  useEffect(() => {
    (async () => {
      setCheckingDomain(true);
      try {
        const { data: d } = await api.get(`/platform/tenants/${t.id}/domain-check`);
        setDomainCheck(d);
      } catch { /* silent */ }
      finally { setCheckingDomain(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id]);
  const [subForm, setSubForm] = useState({
    plan_id: subscription?.plan_id || "",
    status: subscription?.status || "",
    extend_days: "",
    grant_free_days: "",
    custom_price_xof: subscription?.custom_price_xof ?? "",
    notes: subscription?.admin_notes || "",
  });
  const [subSaving, setSubSaving] = useState(false);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié"); } catch {}
  };

  const resetPwd = async () => {
    if (!window.confirm(`Réinitialiser le mot de passe du owner de "${t.name}" ?\n\nL'ancien mot de passe ne fonctionnera plus.`)) return;
    try {
      const { data: r } = await api.post(`/platform/tenants/${t.id}/reset-owner-password`);
      setResetResult(r);
      toast.success("Mot de passe réinitialisé");
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    }
  };

  const saveSubscription = async () => {
    const payload = {};
    if (subForm.plan_id && subForm.plan_id !== subscription?.plan_id) payload.plan_id = subForm.plan_id;
    if (subForm.status && subForm.status !== subscription?.status) payload.status = subForm.status;
    if (subForm.extend_days && Number(subForm.extend_days) > 0) payload.extend_days = Number(subForm.extend_days);
    if (subForm.grant_free_days && Number(subForm.grant_free_days) > 0) payload.grant_free_days = Number(subForm.grant_free_days);
    if (subForm.custom_price_xof !== "" && Number(subForm.custom_price_xof) !== subscription?.custom_price_xof) {
      payload.custom_price_xof = Number(subForm.custom_price_xof);
    }
    if (subForm.notes !== (subscription?.admin_notes || "")) payload.notes = subForm.notes;
    if (Object.keys(payload).length === 0) { toast.info("Aucun changement"); return; }
    setSubSaving(true);
    try {
      await api.patch(`/platform/tenants/${t.id}/subscription`, payload);
      toast.success("Abonnement mis à jour");
      setSubForm((f) => ({ ...f, extend_days: "", grant_free_days: "" }));
      onRefresh();
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    } finally {
      setSubSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={t.name} testid="tenant-detail">
      <div className="p-4 rounded-xl bg-[#e3a03b]/10 text-xs text-[#8a5a10] flex items-center gap-2 mb-4">
        <ShieldCheck className="w-4 h-4" />
        Agrégats anonymisés uniquement — aucun détail de patient, facture ou paiement.
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-5">
        <Field label="Slug" v={<span className="font-mono">{t.slug}</span>} />
        <Field label="Pays" v={t.country || "—"} />
        <Field label="Devise" v={t.currency || "—"} />
        <Field label="Fuseau" v={t.timezone || "—"} />
        <Field label="Créée le" v={formatDateTime(t.created_at)} />
        <Field label="Statut" v={<Badge tone={t.active ? "success" : "danger"}>{t.active ? "Active" : "Suspendue"}</Badge>} />
      </div>

      {/* Owner admin management */}
      <Section title="Propriétaire (owner admin)">
        {resetResult ? (
          <div className="p-4 rounded-xl bg-[#e3a03b]/10 border border-[#e3a03b]/30" data-testid="sa-reset-result">
            <div className="text-xs text-[#8a5a10] uppercase tracking-wider mb-1">Nouveau mot de passe temporaire</div>
            <div className="flex items-center justify-between gap-2">
              <code className="font-mono text-lg text-ink">{resetResult.temp_password}</code>
              <button onClick={() => copy(resetResult.temp_password)} className={BTN_SECONDARY}>
                <Copy className="w-4 h-4" /> Copier
              </button>
            </div>
            <div className="text-xs text-[#8a5a10] mt-2">Email: {resetResult.owner_email} — changement forcé à la reconnexion.</div>
          </div>
        ) : (
          <button onClick={resetPwd} data-testid="sa-reset-owner-btn" className={BTN_SECONDARY}>
            <KeyRound className="w-4 h-4" /> Réinitialiser le mot de passe du owner
          </button>
        )}
      </Section>

      {/* Subscription management */}
      <Section title="Abonnement">
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <Field label="Plan actuel" v={<Badge tone="brand">{subscription?.plan_id || "—"}</Badge>} />
          <Field label="Statut" v={<Badge tone={statusTone(subscription?.status)}>{subscription?.status || "—"}</Badge>} />
          <Field label="Période actuelle" v={subscription?.current_period_end ? formatDateTime(subscription.current_period_end) : "—"} />
          <Field label="Fin essai" v={subscription?.trial_ends_at ? formatDateTime(subscription.trial_ends_at) : "—"} />
          {subscription?.custom_price_xof != null && <Field label="Prix personnalisé" v={`${subscription.custom_price_xof} XOF`} />}
          {subscription?.admin_notes && <Field label="Notes admin" v={subscription.admin_notes} />}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-ink-muted uppercase tracking-wider flex items-center gap-1"><CreditCard className="w-3 h-3" /> Plan</label>
            <select className={INPUT} value={subForm.plan_id} onChange={(e) => setSubForm({ ...subForm, plan_id: e.target.value })} data-testid="sa-sub-plan">
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-ink-muted uppercase tracking-wider">Statut</label>
            <select className={INPUT} value={subForm.status} onChange={(e) => setSubForm({ ...subForm, status: e.target.value })} data-testid="sa-sub-status">
              <option value="">—</option>
              <option value="trial">trial</option>
              <option value="active">active</option>
              <option value="past_due">past_due</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>
          <Input label="Prolonger (jours)" type="number" value={subForm.extend_days}
            onChange={(e) => setSubForm({ ...subForm, extend_days: e.target.value })} testid="sa-sub-extend" icon={CalendarPlus} />
          <Input label="Jours gratuits (essai)" type="number" value={subForm.grant_free_days}
            onChange={(e) => setSubForm({ ...subForm, grant_free_days: e.target.value })} testid="sa-sub-free" icon={Gift} />
          <Input label="Prix personnalisé (XOF)" type="number" value={subForm.custom_price_xof}
            onChange={(e) => setSubForm({ ...subForm, custom_price_xof: e.target.value })} testid="sa-sub-price" />
          <Input label="Notes admin" value={subForm.notes}
            onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })} testid="sa-sub-notes" icon={Pencil} />
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={saveSubscription} disabled={subSaving} className={BTN_PRIMARY} data-testid="sa-sub-save">
            <RefreshCw className={`w-4 h-4 ${subSaving ? "animate-spin" : ""}`} /> Mettre à jour l'abonnement
          </button>
        </div>
      </Section>

      <SubdomainSection tenantId={t.id} slug={t.slug} data={domainCheck} loading={checkingDomain}
        onCheck={async () => {
          setCheckingDomain(true);
          try {
            const { data } = await api.get(`/platform/tenants/${t.id}/domain-check`);
            setDomainCheck(data);
          } catch (err) { toast.error(apiError(err, "Erreur")); }
          finally { setCheckingDomain(false); }
        }}
        onRename={async (newSlug) => {
          try {
            await api.patch(`/platform/tenants/${t.id}/slug`, { slug: newSlug });
            toast.success("Sous-domaine renommé");
            onRefresh();
            // Re-fetch domain check with the new slug
            const { data } = await api.get(`/platform/tenants/${t.id}/domain-check`);
            setDomainCheck(data);
          } catch (err) {
            toast.error(apiError(err, "Erreur lors du renommage"));
            throw err;
          }
        }}
        onCopy={copy}
      />

      <Section title="Agrégats">
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Utilisateurs", v: counts.users, icon: Users },
            { label: "Patients", v: counts.patients, icon: Users },
            { label: "Consultations", v: counts.consultations, icon: Activity },
            { label: "Rendez-vous", v: counts.appointments, icon: Calendar },
            { label: "Factures", v: counts.invoices, icon: BarChart3 },
            { label: "Paiements", v: counts.payments, icon: BarChart3 },
            { label: "Sessions caisse", v: counts.cash_sessions, icon: BarChart3 },
            { label: "Mouvements stock", v: counts.stock_movements, icon: BarChart3 },
            { label: "Produits", v: counts.products, icon: BarChart3 },
          ].map((it, i) => (
            <div key={i} className="p-3 rounded-xl bg-subtle">
              <div className="text-[10px] text-ink-muted uppercase tracking-wider">{it.label}</div>
              <div className="stat-number text-lg font-heading font-bold text-ink">{it.v}</div>
            </div>
          ))}
        </div>
        <Field label="Dernière activité" v={last_activity ? formatDateTime(last_activity) : "—"} />
      </Section>
    </Modal>
  );
}

/* ----------- Helpers ----------- */

function SubdomainSection({ tenantId, slug, data, loading, onCheck, onRename, onCopy }) {
  const [editingSlug, setEditingSlug] = useState(false);
  const [newSlug, setNewSlug] = useState(slug || "");
  const [renaming, setRenaming] = useState(false);

  useEffect(() => { setNewSlug(slug || ""); }, [slug]);

  const submitRename = async () => {
    const v = (newSlug || "").trim().toLowerCase();
    if (!v || v === slug) { setEditingSlug(false); return; }
    if (!/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(v)) {
      toast.error("Slug invalide : lettres minuscules, chiffres, tirets, 3-40 caractères.");
      return;
    }
    if (!window.confirm(`Renommer le sous-domaine en « ${v} » ?\n\nAprès confirmation :\n• L'URL deviendra ${v}.<domaine>\n• Vous devrez créer un nouveau CNAME et re-lier le domaine dans Emergent\n• L'ancien sous-domaine cessera de fonctionner`)) return;
    setRenaming(true);
    try {
      await onRename(v);
      setEditingSlug(false);
    } catch { /* toast already shown */ }
    finally { setRenaming(false); }
  };

  const slugDisplay = (
    <div className="flex items-center gap-2">
      <code className="font-mono text-sm text-ink" data-testid="sa-slug-value">{slug}</code>
      <button onClick={() => setEditingSlug(true)} data-testid="sa-slug-edit-btn"
        className="p-1 rounded hover:bg-subtle text-ink-muted hover:text-brand" title="Renommer">
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  );
  const slugEditor = (
    <div className="flex items-center gap-2" data-testid="sa-slug-edit">
      <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} autoFocus
        className="h-9 rounded-lg border border-[var(--border-default)] bg-white px-2 font-mono text-sm focus:border-brand outline-none w-40"
        placeholder="nouveau-slug" data-testid="sa-slug-input" />
      <button onClick={submitRename} disabled={renaming} data-testid="sa-slug-save"
        className="h-9 px-3 rounded-lg bg-brand text-white text-sm font-semibold disabled:opacity-50">
        {renaming ? "…" : "Enregistrer"}
      </button>
      <button onClick={() => { setEditingSlug(false); setNewSlug(slug || ""); }}
        className="h-9 px-3 rounded-lg border border-[var(--border-default)] text-sm font-semibold">
        Annuler
      </button>
    </div>
  );
  const slugHeader = (
    <div className="flex items-center justify-between mb-3 pb-3 border-b border-[var(--border-default)]">
      <div>
        <div className="text-[10px] text-ink-muted uppercase tracking-wider">Slug (identifiant sous-domaine)</div>
        {editingSlug ? slugEditor : slugDisplay}
      </div>
      <Globe className="w-5 h-5 text-ink-muted" />
    </div>
  );

  if (!data) {
    return (
      <Section title="Sous-domaine (routage par clinique)">
        {slugHeader}
        <div className="text-sm text-ink-muted py-2">
          {loading ? "Vérification…" : "Chargement…"}
        </div>
      </Section>
    );
  }
  if (!data.enabled) {
    return (
      <Section title="Sous-domaine (routage par clinique)">
        {slugHeader}
        <div className="p-3 rounded-xl bg-subtle text-sm text-ink-muted">
          Le routage par sous-domaine n'est pas activé sur ce serveur.
          Ajoutez <code className="font-mono text-xs bg-white px-1">TENANT_DOMAIN_ROOT=votredomaine.com</code>
          dans le fichier <code className="font-mono text-xs bg-white px-1">backend/.env</code> pour l'activer.
          Téléchargez le guide « Sous-domaines par clinique » dans Documentation pour les détails.
        </div>
      </Section>
    );
  }

  const dnsTone =
    data.dns.status === "ok" ? "success" :
    data.dns.status === "missing" ? "danger" : "neutral";
  const httpTone =
    data.http.status === "ok" ? (data.tenant_match ? "success" : "warning") :
    data.http.status === "unreachable" ? "warning" : "neutral";

  const cnameLine = `${data.slug} CNAME ${data.cname_target || "<URL-de-votre-app-Emergent>"}`;

  return (
    <Section title="Sous-domaine (routage par clinique)">
      {slugHeader}
      <div className="space-y-3" data-testid="sa-domain-panel">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] text-ink-muted uppercase tracking-wider">URL publique</div>
            <a href={`https://${data.fqdn}`} target="_blank" rel="noreferrer"
              className="font-mono text-sm text-brand hover:underline break-all" data-testid="sa-domain-fqdn">
              https://{data.fqdn}
            </a>
          </div>
          <button onClick={onCheck} disabled={loading} className="h-10 px-3 rounded-xl border border-[var(--border-default)] bg-white text-ink font-semibold text-sm hover:bg-subtle disabled:opacity-50 flex items-center gap-2"
            data-testid="sa-domain-recheck">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Revérifier
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StatusRow label="DNS" tone={dnsTone}
            text={data.dns.status === "ok" ? `Propagé (${data.dns.detail})` :
                  data.dns.status === "missing" ? "CNAME absent ou non propagé" : "—"} />
          <StatusRow label="Accès HTTPS" tone={httpTone}
            text={data.http.status === "ok"
              ? (data.tenant_match ? "Clinique correctement servie" : "Réponse reçue mais tenant différent — vérifier slug")
              : data.http.status === "unreachable" ? "Pas de réponse (propagation en cours ?)" : "—"} />
        </div>

        <div className="p-3 rounded-xl bg-subtle">
          <div className="text-[10px] text-ink-muted uppercase tracking-wider mb-1">CNAME à créer chez votre registrar DNS</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs flex-1 break-all" data-testid="sa-domain-cname">{cnameLine}</code>
            <button onClick={() => onCopy(cnameLine)} className="p-1.5 rounded hover:bg-white" title="Copier">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {!data.cname_target && (
            <div className="text-[11px] text-ink-muted mt-1 italic">
              Astuce : définissez <code className="font-mono">DOMAIN_CNAME_TARGET</code> dans backend/.env pour pré-remplir automatiquement la valeur.
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function StatusRow({ label, tone, text }) {
  const toneClasses = {
    success: "bg-[#2d8a56]/10 text-[#2d8a56] border-[#2d8a56]/30",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10] border-[#e3a03b]/40",
    danger: "bg-[#d34044]/10 text-[#b12e31] border-[#d34044]/30",
    neutral: "bg-subtle text-ink-muted border-[var(--border-default)]",
  };
  const dot = { success: "bg-[#2d8a56]", warning: "bg-[#e3a03b]", danger: "bg-[#d34044]", neutral: "bg-ink-muted" }[tone];
  return (
    <div className={`p-3 rounded-xl border ${toneClasses[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        {label}
      </div>
      <div className="text-sm mt-1">{text}</div>
    </div>
  );
}

function Modal({ title, children, onClose, testid = "modal" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid={testid}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <h3 className="font-heading font-bold text-lg">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5 p-4 rounded-xl border border-[var(--border-default)]">
      <div className="text-xs text-ink-muted uppercase tracking-wider mb-3 font-semibold">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, v }) {
  return (
    <div>
      <div className="text-[10px] text-ink-muted uppercase tracking-wider">{label}</div>
      <div className="text-ink">{v}</div>
    </div>
  );
}

function Input({ label, testid, icon: Icon, ...props }) {
  return (
    <div>
      <label className="text-xs text-ink-muted uppercase tracking-wider flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      <input {...props} className={INPUT} data-testid={testid} />
    </div>
  );
}
